import { useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { MagicWand, UploadSimple, Sparkle } from "@phosphor-icons/react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

/**
 * Pro-only flyer-to-schedule extractor for owners.
 *
 * Inserted at the top of `ScheduleEditor`, this lets a Pro owner upload an
 * event flyer (PNG/JPG/WEBP) and have Gemini Vision auto-fill the current
 * week's days[] in one shot. The owner reviews the prefilled events
 * inline (existing day rows below) and clicks the regular Save button.
 *
 * - Hidden entirely for non-Pro owners (no teaser, matches Pro UI gating).
 * - Skips `monday`..`sunday` keys with empty arrays so partial flyers
 *   (e.g. only Thu/Fri/Sat) don't blow away other days the owner already
 *   filled in.
 */
export default function FlyerExtractCard({ onDaysExtracted, onTruckNameSuggested }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [extracting, setExtracting] = useState(false);

  if (!user?.is_pro) return null;

  const onPick = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error("Please upload a PNG, JPG, or WEBP image");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large (max 10MB)");
      return;
    }

    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/schedule-extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Only carry forward days the model actually populated, so the owner's
      // existing entries on other days aren't overwritten by empty arrays.
      const filled = {};
      let count = 0;
      for (const d of DAYS) {
        const arr = Array.isArray(data?.days?.[d]) ? data.days[d] : [];
        if (arr.length > 0) {
          filled[d] = arr;
          count += arr.length;
        }
      }

      if (count === 0) {
        toast.error("Couldn't read any events from that flyer — try a clearer image");
        return;
      }

      // Confirm before clobbering — only ask if we have entries to apply.
      const filledDayLabels = Object.keys(filled)
        .map((d) => d[0].toUpperCase() + d.slice(1))
        .join(", ");
      if (!window.confirm(
        `Found ${count} event${count === 1 ? "" : "s"} across ${filledDayLabels}.\n\n` +
        `Replace the current week's events on those days?`,
      )) {
        return;
      }

      onDaysExtracted?.(filled);
      if (data?.truck_name_guess) onTruckNameSuggested?.(data.truck_name_guess);

      toast.success(`Auto-filled ${count} event${count === 1 ? "" : "s"} — review below and Save when ready`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't read that flyer");
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <section
      className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-4 sm:p-5"
      data-testid="flyer-extract-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-primary/15 grid place-items-center flex-none">
          <MagicWand weight="duotone" className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-primary">
            <Sparkle weight="fill" className="h-3 w-3" />
            Pro · auto-fill
          </div>
          <h3 className="font-display font-semibold text-base sm:text-lg mt-0.5">
            Upload a flyer — we'll fill the week
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
            Snap or upload your event flyer and we'll read each day, time, and
            address straight onto your schedule below. You can always edit
            before saving.
          </p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          onChange={onPick}
          disabled={extracting}
          data-testid="flyer-extract-file-input"
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={extracting}
          className="flex-none bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-10 px-4 gap-2"
          data-testid="flyer-extract-upload-btn"
        >
          <UploadSimple weight="bold" className="h-4 w-4" />
          {extracting ? "Reading flyer…" : "Upload flyer"}
        </Button>
      </div>
    </section>
  );
}
